import {
  Component,
  forwardRef,
  ViewChild,
  ElementRef,
  Input,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormGroup,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-avatar-upload',
  standalone: true,
  templateUrl: './avatar-upload.component.html',
  styleUrls: ['./avatar-upload.component.scss'],
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AvatarUploadComponent),
      multi: true,
    },
  ],
})
export class AvatarUploadComponent implements ControlValueAccessor {
  @ViewChild('fileInput') fileInput!: ElementRef;

  @Input() form!: FormGroup; // optional, not required for CVA

  defaultImage = 'assets/img/logo/logo.png';
  preview: string | null = null;     // ✅ keep as string only
  file: File | null = null;

  disabled = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  // ✅ prevents infinite loops / repeated conversions
  private converting = false;
  private lastConvertedUrl: string | null = null;

  triggerFileInput() {
    if (!this.disabled) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) return;

    this.file = file;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string; // data:image/...;base64,...
      this.preview = base64;

      // ✅ store base64 in the form control
      this.onChange(base64);
      this.onTouched();
    };

    reader.readAsDataURL(file);
  }

  /** 🔥 Called when formControl writes a value INTO the component */
  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.file = null;
      this.preview = null;
      return;
    }

    // ✅ if parent writes base64 string
    if (typeof value === 'string' && this.isDataUrl(value)) {
      this.preview = value;
      this.file = null;
      return;
    }

    // ✅ if parent writes URL string -> auto convert to base64
    if (typeof value === 'string' && this.isUrl(value)) {
      this.preview = value;  // show something immediately
      this.file = null;

      // auto-convert (async) and push back to form control
      this.convertUrlAndPushBase64(value);
      return;
    }

    // ✅ if parent writes File
    if (value instanceof File) {
      this.file = value;
      const reader = new FileReader();
      reader.onload = () => (this.preview = reader.result as string);
      reader.readAsDataURL(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // -----------------------
  // Helpers
  // -----------------------

  private isDataUrl(v: string): boolean {
    return v.startsWith('data:image/');
  }

  private isUrl(v: string): boolean {
    return v.startsWith('http://') || v.startsWith('https://') || v.startsWith('/');
  }

  private async convertUrlAndPushBase64(url: string) {
    if (this.converting) return;
    if (this.lastConvertedUrl === url) return;

    this.converting = true;
    this.lastConvertedUrl = url;

    try {
      const base64 = await this.urlToBase64(url);

      this.preview = base64;

      // ✅ this makes the FORM VALUE become base64 even if user didn't change image
      this.onChange(base64);
    } catch (e) {
      // If it fails (often CORS), keep preview as URL
      // and the form will remain URL.
      console.warn('Failed converting image URL to base64 (maybe CORS):', e);
    } finally {
      this.converting = false;
    }
  }

  private async urlToBase64(url: string): Promise<string> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);
    const blob = await res.blob();

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
